import { validateProducts, validateFilters, validateSearchMeta, validateSortOptions } from '../utils/validation';
/**
 * Get country code from Shopify, returns undefined if not available
 */
function getCountryCode() {
    var _a;
    return (typeof window !== 'undefined' && ((_a = window.Shopify) === null || _a === void 0 ? void 0 : _a.country))
        ? window.Shopify.country.toLowerCase()
        : 'au';
}
/**
 * Static list of all essential attribute keys used to populate product data
 * This Set is used to split attributes during extraction for optimal performance
 */
const ESSENTIAL_ATTRIBUTE_KEYS = new Set([
    'secondid',
    'ce_name', 'name', 'title',
    'handle',
    'description', '@description',
    'brand', 'brand_name', 'vendor',
    '_thumburl', 'thumburl',
    'price', 'compare_at_price', 'original_price',
    'available_for_sale',
    'online_stock', // BDN-979: FH attribute mirroring PDP's location-restricted stock check
    'custom_colour', 'custom_colour_group', 'option_color',
    'option_size', 'shopify_size',
    'variant_details' // Custom attribute for variant data (This is Fredhopper custom work for Birdsnest only)
]);
/**
 * Extract attributes from item and split into essential and custom attributes in a single loop
 * Matches the original JavaScript implementation from Fredhopper Shopify app:
 * - Iterates through all item.attribute once
 * - Always takes the first value from attr.value[0]
 * - Uses non-ml fallback if value is not available
 * - When attr.value.length > 1, keeps the mapped string array
 * - Splits attributes into essential (for product data) and custom (for extraAttrs) during the loop
 *
 * @param item - The Fredhopper item to extract attributes from
 * @param essentialKeys - Set of essential attribute keys (pre-computed for performance)
 * @returns Object containing attrs (for product mapping) and extraAttrs
 */
function extractItemAttributes(item, essentialKeys) {
    const attrs = {};
    const extraAttrs = {};
    // Single loop: process attributes and split into essential vs custom
    if (Array.isArray(item.attribute)) {
        for (const attr of item.attribute) {
            // Handle array values, with non_ml fallback (matching Fredhopper Shopify app implementation)
            // Note: According to the API response, value is always an array.
            let value = '';
            // Related colour product data from the Fredhopper API (This is Fredhopper custom work for Birdsnest only)
            if (attr.value && attr.name === 'swatch_info') {
                try {
                    // attr 'value' for swatch_info is always [string] with 1 item only
                    const firstValue = attr.value[0];
                    value = (firstValue === null || firstValue === void 0 ? void 0 : firstValue.value) || (firstValue === null || firstValue === void 0 ? void 0 : firstValue['non-ml']) || '';
                    extraAttrs['swatch_info'] = value ? JSON.parse(value) : null;
                }
                catch (e) {
                    console.error('[FH Mapper] Failed to parse swatch_info:', e);
                    extraAttrs['swatch_info'] = null;
                }
                continue;
            }
            // Handle array values, with non_ml fallback (matching Fredhopper Shopify app implementation)
            // Note: According to the API response, value is always an array.
            // Split attributes during the loop: essential attributes go to attrs, others to extraAttrs
            if (attr.value) {
                if (essentialKeys.has(attr.name)) {
                    // attr 'value' for essentialKeys attributes should always be converted to string
                    const firstValue = attr.value[0];
                    value = (firstValue === null || firstValue === void 0 ? void 0 : firstValue.value) || (firstValue === null || firstValue === void 0 ? void 0 : firstValue['non-ml']) || '';
                    attrs[attr.name] = value;
                }
                else {
                    value = attr.value.map((v) => { var _a, _b; return (_b = (_a = v === null || v === void 0 ? void 0 : v.value) !== null && _a !== void 0 ? _a : v === null || v === void 0 ? void 0 : v['non-ml']) !== null && _b !== void 0 ? _b : ''; });
                    extraAttrs[attr.name] = value;
                }
            }
        }
    }
    return { attrs, extraAttrs };
}
/**
 * Map variant details from Fredhopper API to ProductVariant format
 * Custom work for Birdsnest: Maps variant_details attribute (JSON string) to ProductVariant[]
 * Note: Price is set to null as all variant prices are the same as the product price
 * @param variantDetails - Array of FhrVariantDetails parsed from variant_details attribute
 * @returns Array of ProductVariant objects
 */
function mapVariants(variantDetails) {
    if (!Array.isArray(variantDetails) || variantDetails.length === 0) {
        return [];
    }
    return variantDetails.map((variant) => {
        var _a;
        return ({
            id: variant.variant_id,
            title: variant.variant_title,
            sku: variant.sku,
            price: {
                regular: -1, // Birdsnest confirms that all variant prices are the same as the product price. Use -1 to bypass schema validation.
                sale: undefined,
                special: undefined
            },
            // BDN-979: prefer online_stock (matches PDP's location-restricted stock check);
            // fall back to available_for_sale while online_stock rolls out in FH Staging.
            available: ((_a = variant.online_stock) !== null && _a !== void 0 ? _a : variant.available_for_sale) === 'true',
            selectedOptions: variant.option_size ? [
                { name: 'Size', value: variant.option_size }
            ] : [],
        });
    });
}
export function mapProducts(items, externalAttributes) {
    if (!items || !Array.isArray(items)) {
        console.warn('[FH Mapper] No items or items is not an array');
        return [];
    }
    // Get country code once for all items (performance optimization)
    const countryCode = getCountryCode();
    // Build essential keys set once (performance optimization)
    const essentialKeys = countryCode
        ? new Set([...ESSENTIAL_ATTRIBUTE_KEYS, `price_${countryCode}`, `compare_at_price_${countryCode}`])
        : ESSENTIAL_ATTRIBUTE_KEYS;
    const mappedProducts = items.map((item) => {
        var _a;
        // Extract and split attributes in a single loop - attrs for product data, extraAttrs for unused
        const { attrs, extraAttrs } = extractItemAttributes(item, essentialKeys);
        // Extract product data using the same fallback chain as original
        const id = item.id || attrs.secondid || 'unknown';
        // Title: ce_name || name || title (matching original)
        const title = attrs.ce_name || attrs.name || attrs.title || 'Untitled Product';
        // Handle: handle || item.id
        const handle = attrs.handle || item.id || '';
        // Description: description || @description
        const description = attrs.description || attrs['@description'] || '';
        // Brand: brand || brand_name || vendor (matching original)
        const brand = attrs.brand || attrs.brand_name || attrs.vendor || '';
        // Image: _thumburl || thumburl (matching original)
        const imageUrl = attrs._thumburl || attrs.thumburl || '';
        // Price: country-specific with fallbacks (matching original implementation)
        let price = 0;
        let compareAtPrice = 0;
        if (countryCode) {
            const priceField = `price_${countryCode}`;
            const compareAtPriceField = `compare_at_price_${countryCode}`;
            price = parseFloat(attrs[priceField] || attrs.price || '0') || 0;
            compareAtPrice = parseFloat(attrs[compareAtPriceField] || attrs.compare_at_price || attrs.original_price || '0') || 0;
        }
        else {
            // Fallback if no country info available
            price = parseFloat(attrs.price || '0') || 0;
            compareAtPrice = parseFloat(attrs.compare_at_price || attrs.original_price || '0') || 0;
        }
        // Availability: online_stock as string 'true'/'false' (BDN-979 — mirrors PDP's
        // location-restricted stock check). Falls back to available_for_sale while
        // online_stock rolls out in FH Staging.
        const onlineStock = (_a = attrs.online_stock) !== null && _a !== void 0 ? _a : attrs.available_for_sale;
        const availability = onlineStock === 'true' ? 'in_stock' :
            onlineStock === 'false' ? 'out_of_stock' :
                'in_stock';
        // Color: custom_colour || custom_colour_group || option_color
        const color = attrs.custom_colour || attrs.custom_colour_group || attrs.option_color || '';
        // Size: option_size || shopify_size (matching original: takes first value only)
        // Note: Fredhopper Shopify app implementation doesn't extract all size values, just the first one
        // We put it in an array to match the Product interface requirement (unless it's already an array)
        const sizeValue = attrs.option_size || attrs.shopify_size || '';
        const sizes = Array.isArray(sizeValue) ? sizeValue : (sizeValue ? [sizeValue] : ['']);
        // URL: from detail link or construct from handle
        const detailLink = Array.isArray(item.link)
            ? item.link.find(l => l.type === 'detail')
            : null;
        const productUrl = (detailLink === null || detailLink === void 0 ? void 0 : detailLink['url-params']) || (handle ? `/products/${handle}` : '');
        // Custom attribute for variant data (This is Fredhopper custom work for Birdsnest only)
        let variantDetails = [];
        try {
            variantDetails = JSON.parse(attrs.variant_details || '[]');
        }
        catch (_b) {
            variantDetails = [];
        }
        const variants = mapVariants(variantDetails);
        // Merge all attributes from externalAttributes if available
        // External attributes are custom attributes that are not part of Fredhopper API response.
        // For Birdsnest, external attributes contain 'heart_score' value for Style Matches 'heart' element.
        if (externalAttributes === null || externalAttributes === void 0 ? void 0 : externalAttributes[id]) {
            Object.assign(extraAttrs, externalAttributes[id]);
        }
        const data = {
            id: String(id),
            title,
            handle,
            brand,
            color,
            size: sizes, // sizes is always an array, no need to check length
            price: {
                regular: price >= compareAtPrice ? price : compareAtPrice,
                sale: price >= compareAtPrice ? undefined : price,
                special: undefined
            },
            variants: variants,
            image: {
                url: imageUrl,
                alt: `${title}-image`,
            },
            availability,
            description,
            customAttributes: extraAttrs, // Always assign, even if empty {} - creating empty object is very cheap
        };
        return data;
    });
    return validateProducts(mappedProducts, 'Fredhopper');
}
/**
 * Essential attribute keys from suggest API used to populate product data.
 * Any other keys on the suggestion object go to customAttributes.
 * @see ./README/FredhopperSuggestExampleResponses.md
 */
const ESSENTIAL_SUGGESTION_ATTRIBUTE_KEYS = new Set([
    'secondId',
    'title',
    'handle',
    'vendor',
    '_imageurl',
    'custom_colour',
]);
/**
 * Map suggest API product suggestions to Product[].
 * Uses ESSENTIAL_SUGGESTION_ATTRIBUTE_KEYS to split mapped fields vs customAttributes (same approach as mapProducts).
 * @see ./README/FredhopperSuggestExampleResponses.md
 */
export function mapSuggestedProducts(suggestProducts) {
    if (!(suggestProducts === null || suggestProducts === void 0 ? void 0 : suggestProducts.length))
        return [];
    const countryCode = getCountryCode();
    // Build essential keys set once (include dynamic price keys like mapProducts)
    const essentialKeys = countryCode
        ? new Set([...ESSENTIAL_SUGGESTION_ATTRIBUTE_KEYS, `price_${countryCode}`, `compare_at_price_${countryCode}`])
        : ESSENTIAL_SUGGESTION_ATTRIBUTE_KEYS;
    const mapped = suggestProducts.map((s) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        const suggestionRecord = s;
        const id = String((_a = suggestionRecord.secondId) !== null && _a !== void 0 ? _a : '');
        const title = String((_b = suggestionRecord.title) !== null && _b !== void 0 ? _b : '');
        const handle = String((_c = suggestionRecord.handle) !== null && _c !== void 0 ? _c : '');
        const brand = String((_d = suggestionRecord.vendor) !== null && _d !== void 0 ? _d : '');
        const color = String((_e = suggestionRecord.custom_colour) !== null && _e !== void 0 ? _e : '');
        const imageUrl = String((_f = suggestionRecord._imageurl) !== null && _f !== void 0 ? _f : '');
        // Price: country-specific with fallbacks (same as mapProducts)
        let priceVal = 0;
        let compareAtPriceVal = 0;
        if (countryCode) {
            const priceField = `price_${countryCode}`;
            const compareAtPriceField = `compare_at_price_${countryCode}`;
            priceVal = parseFloat(String((_h = (_g = suggestionRecord[priceField]) !== null && _g !== void 0 ? _g : suggestionRecord.price) !== null && _h !== void 0 ? _h : '')) || 0;
            compareAtPriceVal = parseFloat(String((_l = (_k = (_j = suggestionRecord[compareAtPriceField]) !== null && _j !== void 0 ? _j : suggestionRecord.compare_at_price) !== null && _k !== void 0 ? _k : suggestionRecord.original_price) !== null && _l !== void 0 ? _l : '')) || 0;
        }
        else {
            priceVal = parseFloat(String((_m = suggestionRecord.price) !== null && _m !== void 0 ? _m : '')) || 0;
            compareAtPriceVal = parseFloat(String((_p = (_o = suggestionRecord.compare_at_price) !== null && _o !== void 0 ? _o : suggestionRecord.original_price) !== null && _p !== void 0 ? _p : '')) || 0;
        }
        const size = [];
        const price = {
            regular: priceVal >= compareAtPriceVal ? priceVal : compareAtPriceVal,
            sale: priceVal >= compareAtPriceVal ? undefined : priceVal,
            special: undefined
        };
        const customAttributes = {};
        for (const [k, v] of Object.entries(suggestionRecord)) {
            if (!essentialKeys.has(k) && v !== undefined)
                customAttributes[k] = v;
        }
        const data = {
            id,
            title,
            handle,
            brand,
            color,
            size,
            price,
            image: { url: imageUrl, alt: title },
            availability: 'in_stock',
            customAttributes: Object.keys(customAttributes).length ? customAttributes : undefined,
        };
        return data;
    });
    return validateProducts(mapped, 'Fredhopper');
}
export function mapFilters(filters, currentFilters) {
    const mappedFilters = [];
    if (!filters || !Array.isArray(filters)) {
        console.warn('[FH Mapper] No filters or filters is not an array');
        return mappedFilters;
    }
    filters.forEach((filter) => {
        var _a;
        // Use facet.on as primary (matching Fredhopper Shopify app implementation)
        const filterId = filter.on || filter.facetid;
        const displayName = filter.title || filterId;
        const baseFilter = {
            id: filterId,
            label: displayName,
            type: 'select',
            options: []
        };
        // Detect display hint for different UI types (matching Fredhopper Shopify app implementation)
        let displayHint = filter['display-hint'];
        let isMultiSelect = false;
        let isMultiOr = false;
        let isSlider = false;
        if (Array.isArray(displayHint)) {
            isMultiSelect = displayHint.includes('multiselect-and');
            isMultiOr = displayHint.includes('multiselect-or');
            isSlider = displayHint.includes('slider');
        }
        else if (typeof displayHint === 'string') {
            isMultiSelect = displayHint === 'multiselect-and';
            isMultiOr = displayHint === 'multiselect-or';
            isSlider = displayHint === 'slider';
        }
        // Handle range filters (price, etc.) - matching Fredhopper Shopify app implementation slider logic
        if (isSlider && filter['globalrange']) {
            baseFilter.type = 'range';
            baseFilter.min = filter['globalrange'].min;
            baseFilter.max = filter['globalrange'].max;
            baseFilter.currentMin = filter['globalrange'].min;
            baseFilter.currentMax = filter['globalrange'].max;
        }
        // Handle select filters with values
        else if (filter.filtersection && Array.isArray(filter.filtersection)) {
            // Determine filter type based on basetype and display hints
            if (filter.basetype === 'set') {
                baseFilter.type = (isMultiSelect || isMultiOr) ? 'select' : 'select';
            }
            else {
                baseFilter.type = 'select';
            }
            baseFilter.options = filter.filtersection.map((section) => {
                var _a, _b, _c;
                // Extract value: section.value.value (matching Fredhopper Shopify app implementation)
                const value = ((_a = section.value) === null || _a === void 0 ? void 0 : _a.value) || '';
                // Extract label: section.link.name (matching Fredhopper Shopify app implementation)
                const label = ((_b = section.link) === null || _b === void 0 ? void 0 : _b.name) || value;
                // Extract count: section.nr (matching Fredhopper Shopify app implementation)
                const count = section.nr || 0;
                // Check if this value is selected
                const selected = ((_c = currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters[filterId]) === null || _c === void 0 ? void 0 : _c.includes(value)) || false;
                return {
                    id: String(value),
                    label,
                    count: Number(count),
                    selected,
                    value: String(value)
                };
            });
        }
        // Check for custom-fields.custom-field and add to customAttributes.
        // Also append filter.basetype (e.g. "set", "text", "tree") so consumers
        // can decide things like Fredhopper filter syntax (`attr=value` for single-select
        // text facets vs `attr>{val1;val2}` for multi-select set facets).
        const customFields = (_a = filter['custom-fields']) === null || _a === void 0 ? void 0 : _a['custom-field'];
        const customAttributes = (customFields === null || customFields === void 0 ? void 0 : customFields.length) ? [...customFields] : [];
        if (filter.basetype) {
            customAttributes.push({ name: 'basetype', value: filter.basetype });
        }
        if (customAttributes.length) {
            baseFilter.customAttributes = customAttributes;
        }
        mappedFilters.push(baseFilter);
    });
    return validateFilters(mappedFilters, 'Fredhopper');
}
/**
 * Extract merchandising from universe themes. Only themes with campaign_slot (campaignSlotField) are added.
 * Each such theme → one MerchandisingApplied. rules: id = campaign_slot.value, name = theme.name, description = theme.slogan.
 * If theme.items?.item has at least 1 item: pinnedProducts = mapProducts(items); else customContent only.
 * customContent is an object with custom-fields, static-content, and link from the theme.
 * Example FH API response structure for campaign_slot: see FredhopperQueryExampleResponses.md.
 */
function extractMerchandisingApplied(universe) {
    var _a, _b, _c, _d, _e, _f;
    const merchandisingApplied = [];
    if (!((_a = universe === null || universe === void 0 ? void 0 : universe.themes) === null || _a === void 0 ? void 0 : _a.length))
        return merchandisingApplied;
    for (const themeContainer of universe.themes) {
        if (!Array.isArray(themeContainer.theme))
            continue;
        for (const theme of themeContainer.theme) {
            const campaignSlotField = (_c = (_b = theme['custom-fields']) === null || _b === void 0 ? void 0 : _b['custom-field']) === null || _c === void 0 ? void 0 : _c.find((field) => field.name === 'campaign_slot');
            if (!campaignSlotField)
                continue;
            const items = (_e = (_d = theme.items) === null || _d === void 0 ? void 0 : _d.item) !== null && _e !== void 0 ? _e : [];
            const rules = [
                {
                    id: (_f = campaignSlotField === null || campaignSlotField === void 0 ? void 0 : campaignSlotField.value) !== null && _f !== void 0 ? _f : '',
                    name: theme.name,
                    description: theme.slogan,
                    type: items.length >= 1 ? 'product' : 'custom',
                },
            ];
            const customContent = [
                {
                    'custom-fields': theme['custom-fields'],
                    'static-content': theme['static-content'],
                    link: theme.link,
                },
            ];
            if (items.length >= 1) {
                merchandisingApplied.push({
                    rules,
                    pinnedProducts: mapProducts(items),
                    customContent,
                });
            }
            else {
                merchandisingApplied.push({
                    rules,
                    customContent,
                });
            }
        }
    }
    return merchandisingApplied;
}
export function mapMeta(data, page, pageSize) {
    var _a, _b, _c;
    // Find the selected universe (matching Fredhopper Shopify app implementation)
    let universe = null;
    if (((_a = data.universes) === null || _a === void 0 ? void 0 : _a.universe) && ((_b = data.info) === null || _b === void 0 ? void 0 : _b['current-universe'])) {
        universe = data.universes.universe.find(u => u.name === data.info['current-universe']);
    }
    if (!universe && Array.isArray((_c = data.universes) === null || _c === void 0 ? void 0 : _c.universe)) {
        universe = data.universes.universe[0]; // fallback
    }
    const itemsSection = universe === null || universe === void 0 ? void 0 : universe['items-section'];
    const results = itemsSection === null || itemsSection === void 0 ? void 0 : itemsSection.results;
    // Extract total items (matching Fredhopper Shopify app implementation: total-items from results)
    const totalResults = (results === null || results === void 0 ? void 0 : results['total-items']) || 0;
    const totalPages = Math.ceil(totalResults / pageSize);
    const merchandisingApplied = universe ? extractMerchandisingApplied(universe) : undefined;
    // Style Matches: when recommendations are still being computed by the Peacock API,
    // the response shape is `{ message, percent_complete, state: "building" }` rather
    // than the standard Fredhopper response. Surface percent_complete so the UI can
    // render the building/loading state.
    const rawPercentComplete = data === null || data === void 0 ? void 0 : data.percent_complete;
    const styleMatchesPercentComplete = typeof rawPercentComplete === 'number' ? rawPercentComplete : undefined;
    // Redirect targets — surfaced by the Birdsnest middleware at the top level
    // of the response when the search resolves to a single matching collection
    // / page / product (alongside `external_attributes` and `percent_complete`).
    const collection = (data === null || data === void 0 ? void 0 : data.collection) && typeof data.collection === 'object' ? data.collection : undefined;
    const pageMeta = (data === null || data === void 0 ? void 0 : data.page) && typeof data.page === 'object' ? data.page : undefined;
    const product = (data === null || data === void 0 ? void 0 : data.product) && typeof data.product === 'object' ? data.product : undefined;
    return validateSearchMeta(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ totalResults,
        totalPages, currentPage: page, pageSize }, ((merchandisingApplied === null || merchandisingApplied === void 0 ? void 0 : merchandisingApplied.length) ? { merchandisingApplied } : {})), (styleMatchesPercentComplete !== undefined ? { styleMatchesPercentComplete } : {})), (collection ? { collection } : {})), (pageMeta ? { page: pageMeta } : {})), (product ? { product } : {})), 'Fredhopper');
}
export function mapSorts() {
    return validateSortOptions([
        { id: 'relevance', label: 'Relevance' },
        { id: 'price-asc', label: 'Price: Low to High' },
        { id: 'price-desc', label: 'Price: High to Low' },
        { id: 'title-asc', label: 'Name: A to Z' },
        { id: 'title-desc', label: 'Name: Z to A' },
    ], 'Fredhopper');
}
